import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import type { EmailMeta } from "@/api/email";
import { DryRunNotice, ScopeBadge } from "@/components/email/EmailBadges";
import { RouteTabs } from "@/components/email/RouteTabs";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useEmailMeta } from "@/hooks/useEmailMeta";
import { usePermissions } from "@/hooks/usePermissions";
import { campaignsPath, emailBase, legacyEmailTarget, outreachPath } from "@/lib/emailRoutes";

export interface EmailOutletContext {
  slug: string;
  meta: EmailMeta | null;
  error: string | null;
}

export function useEmailOutlet(): EmailOutletContext {
  return useOutletContext<EmailOutletContext>();
}

/** Leaves the Email module when the URL's project role does not open it. */
export function useEmailAccessGuard(slug: string | undefined) {
  const navigate = useNavigate();
  const perms = usePermissions();
  const { project } = useCurrentProject();
  const projectLoaded = !!slug && project?.slug === slug;
  useEffect(() => {
    if (projectLoaded && !perms.canViewEmail) {
      toast.info("You don't have access to the Email module.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [projectLoaded, slug, perms.canViewEmail, navigate]);
  return perms.canViewEmail;
}

/**
 * The Email module of a project: Campaigns | Outreach. A campaign opens in
 * its own workspace (Sequence | Leads | Launch | Performance). Everything
 * here is this project's, as the backend guarantees; Lemlist only sends.
 */
export default function EmailLayout() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const allowed = useEmailAccessGuard(slug);
  const { meta, access, error } = useEmailMeta(slug);

  if (!allowed || !slug) return null;
  const section = location.pathname.replace(/\/+$/, "").endsWith("/outreach") ? "outreach" : "campaigns";

  return (
    <div className="space-y-5 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-cta">
            <Mail className="h-[18px] w-[18px]" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Email</h1>
            <p className="flex items-center gap-1.5 text-sm text-slate-500">
              {access && <ScopeBadge scope={access.scope} />}
              Campaigns and their outreach in {access?.name ?? "this project"}.
            </p>
          </div>
        </div>
        {meta && !meta.external_effects && <DryRunNotice compact />}
      </header>

      {error && <div className="rounded-plane bg-white p-6 text-sm text-rose-600 shadow-plane">{error}</div>}

      <RouteTabs
        label="Email"
        value={section}
        tabs={[
          { key: "campaigns", label: "Campaigns", to: campaignsPath(slug) },
          { key: "outreach", label: "Outreach", to: outreachPath(slug) },
        ]}
      />

      <Outlet context={{ slug, meta, error } satisfies EmailOutletContext} />
    </div>
  );
}

/** `/email` and its former query URLs (`?tab=contacts|templates`,
 *  `?campaign=ID`) land on their place in the new module; an unknown
 *  section lands on Campaigns. */
export function EmailIndexRedirect({ unknown = false }: { unknown?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  if (!slug) return null;
  const target = unknown ? "campaigns" : legacyEmailTarget(location.search);
  return <Navigate to={`${emailBase(slug)}/${target}`} replace />;
}
